import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faArrowAltDown, faArrowAltUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Badge,
  Group,
  HoverCard,
  Paper,
  Skeleton,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";

import { SetUpCIUpsell } from "../SetUpCIUpsell";

export interface HeaderCardProps {
  title: string;
  current?: number;
  diff?: number;
  icon: IconDefinition;
  href?: string;
  percent?: boolean;
  highIsGood?: boolean;
  blurred?: boolean;
}

const displayNumberAsStyle = (value: number, percent: boolean = false) => {
  if (percent) {
    return value.toLocaleString(undefined, {
      style: "percent",
      minimumFractionDigits: 0,
    });
  } else {
    return value.toLocaleString();
  }
};

interface DiffBadgeProps {
  diff: number;
  percent: boolean;
  highIsGood: boolean;
}

const DiffBadge: React.FC<DiffBadgeProps> = ({ diff, percent, highIsGood }) => {
  const theme = useMantineTheme();

  let prefix = "+";
  let icon = undefined;
  let color = theme.primaryColor;

  if (diff > 0) {
    icon = <FontAwesomeIcon icon={faArrowAltUp} />;
    color = highIsGood ? "green" : "red";
  } else if (diff < 0) {
    prefix = "";
    icon = <FontAwesomeIcon icon={faArrowAltDown} />;
    color = highIsGood ? "red" : "green";
  }

  return (
    <Badge rightSection={icon} color={color}>
      {`${prefix}${displayNumberAsStyle(diff, percent)}`}
    </Badge>
  );
};

export const HeaderCard: React.FC<HeaderCardProps> = ({
  title,
  current,
  diff,
  icon,
  href,
  percent = false,
  highIsGood = false,
  blurred = false,
}) => {
  const theme = useMantineTheme();
  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      component="a"
      href={href}
      sx={
        href ? { "&:hover": { backgroundColor: theme.colors["gray"][0] } } : {}
      }
    >
      <Group position="center">
        <ThemeIcon
          variant="gradient"
          gradient={{ from: "blue.8", to: "blue.5", deg: 135 }}
          size={56}
          radius="xl"
        >
          <FontAwesomeIcon icon={icon} size="2x" fixedWidth color="white" />
        </ThemeIcon>
        <div data-testid={`info-${title.toLowerCase().split(" ").join("-")}`}>
          <Title order={2} size="h3" weight={400}>
            {title}
          </Title>
          {current === undefined || diff === undefined ? (
            <Skeleton height={20} width={40} my="sm" />
          ) : blurred ? (
            <HoverCard withArrow shadow="sm" width={180}>
              <HoverCard.Dropdown p="md">
                <SetUpCIUpsell toTrackItemName="fix rate" />
              </HoverCard.Dropdown>
              <HoverCard.Target>
                <Title order={3} size="h1" sx={{ display: "inline" }}>
                  N/A
                </Title>
              </HoverCard.Target>
            </HoverCard>
          ) : (
            <Group spacing="sm" data-testid="info-summary">
              <Title order={3} size="h1" data-testid="info-summary-number">
                {displayNumberAsStyle(current, percent)}
              </Title>
              {diff !== 0 && (
                <DiffBadge
                  diff={diff}
                  percent={percent}
                  highIsGood={highIsGood}
                />
              )}
            </Group>
          )}
        </div>
      </Group>
    </Paper>
  );
};
